/**
 * ClientOrdersSection Component
 *
 * Reusable section component for displaying client orders list with loading state.
 * Wraps ClientOrdersList with container styling and loading/empty states.
 *
 * FEATURES:
 * - Built-in loading state with spinner/skeleton
 * - Empty state handling
 * - Container styling (container-auto, padding)
 * - Full integration with ClientOrdersList
 * - Customizable via props
 *
 * USAGE:
 * <ClientOrdersSection
 *   orders={orders}
 *   isLoading={isLoading}
 *   categoryName="Cleaning"
 *   subcategoryName="Home Cleaning"
 * />
 *
 * @example
 * // Basic usage
 * <ClientOrdersSection
 *   orders={ordersList}
 *   isLoading={false}
 * />
 *
 * @example
 * // With category context
 * <ClientOrdersSection
 *   orders={orders}
 *   isLoading={loading}
 *   categoryName="Repair Services"
 *   subcategoryName="Appliance Repair"
 * />
 *
 * @example
 * // Custom styling and display count
 * <ClientOrdersSection
 *   orders={orders}
 *   isLoading={false}
 *   initialDisplayCount={8}
 *   containerClassName="bg-surface-sunken py-16"
 *   listClassName="max-w-6xl mx-auto"
 * />
 *
 * @example
 * // Custom loading message
 * <ClientOrdersSection
 *   orders={[]}
 *   isLoading={true}
 *   loadingMessage="Loading available orders..."
 * />
 *
 * @example
 * // Custom empty state
 * <ClientOrdersSection
 *   orders={[]}
 *   isLoading={false}
 *   emptyTitle="No orders available"
 *   emptyMessage="Check back soon for new opportunities"
 * />
 */

'use client';

import { type HTMLAttributes } from 'react';
import { cn } from '@/src/shared/lib/utils';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { ClientOrdersList } from '@/src/widgets/orders/ClientOrdersList';
import type { OrderCardProps } from '@/src/entities/order/ui/OrderCard';

export interface ClientOrdersSectionProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * Array of orders to display
   */
  orders: OrderCardProps[];

  /**
   * Loading state - shows loading message when true
   * @default false
   */
  isLoading?: boolean;

  /**
   * Category name for context (passed to ClientOrdersList)
   */
  categoryName?: string;

  /**
   * Subcategory name for context (passed to ClientOrdersList)
   */
  subcategoryName?: string;

  /**
   * Initial number of orders to display before "Load More"
   * @default 4
   */
  initialDisplayCount?: number;

  /**
   * Custom loading message
   * @default "Loading orders..."
   */
  loadingMessage?: string;

  /**
   * Custom empty state title
   * @default "No orders found"
   */
  emptyTitle?: string;

  /**
   * Custom empty state message
   * @default "There are no client orders for this category yet"
   */
  emptyMessage?: string;

  /**
   * Show section even if empty (for debugging or layout purposes)
   * @default false
   */
  showWhenEmpty?: boolean;

  /**
   * Custom container classes
   */
  containerClassName?: string;

  /**
   * Custom classes for ClientOrdersList
   */
  listClassName?: string;

  /**
   * Callback when worker clicks respond button on an order
   */
  onRespond?: (orderId: string) => void;

  /**
   * Callback when order card is clicked (for viewing details)
   */
  onClick?: (orderId: string) => void;

  /**
   * Callback when owner clicks "Renew" on their own order in the discovery feed
   */
  onRenew?: (orderId: string) => void;
}

/**
 * ClientOrdersSection Component
 *
 * Section wrapper for ClientOrdersList with loading and empty states.
 * Provides consistent layout across pages that display client orders.
 */
export const ClientOrdersSection: React.FC<ClientOrdersSectionProps> = ({
  orders,
  isLoading = false,
  categoryName,
  subcategoryName,
  initialDisplayCount = 4,
  loadingMessage = 'Loading orders...',
  emptyTitle = 'No orders found',
  emptyMessage = 'There are no client orders for this category yet',
  showWhenEmpty = false,
  containerClassName,
  listClassName,
  onRespond,
  onClick,
  onRenew,
  className,
  ...props
}) => {
  // Don't render if empty and showWhenEmpty is false
  if (!isLoading && orders.length === 0 && !showWhenEmpty) {
    return null;
  }

  return (
    <section
      className={cn('container-auto py-8', containerClassName, className)}
      {...props}
    >
      {/* Loading State */}
      {isLoading ? (
        <div className="text-center py-12">
          <Typography variant="h3" color="secondary">
            {loadingMessage}
          </Typography>
        </div>
      ) : orders.length > 0 ? (
        /* Client Orders List */
        <ClientOrdersList
          orders={orders}
          categoryName={categoryName}
          subcategoryName={subcategoryName}
          initialDisplayCount={initialDisplayCount}
          className={listClassName}
          onRespond={onRespond}
          onClick={onClick}
          onRenew={onRenew}
        />
      ) : (
        /* Empty State */
        <div className="text-center py-12">
          <Typography variant="h3" color="secondary">
            {emptyTitle}
          </Typography>
          <Typography variant="body" color="secondary" className="mt-2">
            {emptyMessage}
          </Typography>
        </div>
      )}
    </section>
  );
};

ClientOrdersSection.displayName = 'ClientOrdersSection';

// Re-export types for convenience
export type { OrderCardProps };
